import { useState, useEffect, useCallback } from "react";
import { Mail, FileText, Send, Bell, Plus, Trash2, Edit2, AlertTriangle, CheckCircle } from "lucide-react";
import AppShell from "../components/layout/AppShell";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import ComposeEmail from "../components/communication/ComposeEmail";
import TemplateEditor from "../components/communication/TemplateEditor";
import ReminderModal from "../components/communication/ReminderModal";

var GROUP_LABELS = {
  todos: "Todos", consorcio: "Consorcio", seguros: "Seguros",
  plano_saude: "Plano de Saude", leads_abertos: "Leads", individual: "Individual",
};
var TYPE_LABELS = {
  vencimento_boleto: "Vencimento Boleto", vencimento_cartao: "Vencimento Cartao",
  marketing: "Marketing", custom: "Personalizado",
};

function StatusBadge({ configured }) {
  if (configured) return (
    <span className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700" data-testid="email-status-badge">
      <CheckCircle className="w-3 h-3" /> Resend ativo
    </span>
  );
  return (
    <span className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700" data-testid="email-status-badge">
      <AlertTriangle className="w-3 h-3" /> Modo simulado (sem API key)
    </span>
  );
}

export default function CommunicationPage() {
  var auth = useAuth();
  const [tab, setTab] = useState("compose");
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [toast, setToast] = useState(null);

  var fetchData = useCallback(async function() {
    try {
      var res = await Promise.all([
        api.get("/emails/templates"),
        api.get("/emails/campaigns"),
        api.get("/emails/status"),
      ]);
      setTemplates(res[0].data.templates || []);
      setCampaigns(res[1].data.campaigns || []);
      setEmailStatus(res[2].data);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(function() { fetchData(); }, [fetchData]);

  function showToast(msg, type) {
    setToast({ msg: msg, type: type });
    setTimeout(function() { setToast(null); }, 4000);
  }

  async function handleSendCampaign(data) {
    setLoading(true);
    try {
      var res = await api.post("/emails/campaigns/send", data);
      showToast(res.data.message, "success");
      setTab("history");
      fetchData();
    } catch(e) {
      var detail = e.response?.data?.detail || "Erro ao enviar";
      showToast(typeof detail === "string" ? detail : "Erro ao enviar", "error");
    } finally { setLoading(false); }
  }

  async function handleSaveTemplate(data, id) {
    setLoading(true);
    try {
      if (id) { await api.put("/emails/templates/" + id, data); }
      else { await api.post("/emails/templates", data); }
      setShowTemplateEditor(false);
      setEditTemplate(null);
      fetchData();
      showToast("Template salvo!", "success");
    } catch(e) {
      showToast("Erro ao salvar template", "error");
    } finally { setLoading(false); }
  }

  async function handleDeleteTemplate(id) {
    try {
      await api.delete("/emails/templates/" + id);
      fetchData();
      showToast("Template excluido", "success");
    } catch(e) { showToast("Erro ao excluir", "error"); }
  }

  async function handleDeleteCampaign(id) {
    try {
      await api.delete("/emails/campaigns/" + id);
      fetchData();
    } catch(e) { console.error(e); }
  }

  async function handleSendReminder(data) {
    setLoading(true);
    try {
      var res = await api.post("/emails/reminders/send", data);
      showToast(res.data.message, "success");
      setShowReminder(false);
    } catch(e) {
      var detail = e.response?.data?.detail || "Erro ao enviar lembretes";
      showToast(typeof detail === "string" ? detail : "Erro", "error");
    } finally { setLoading(false); }
  }

  var configured = emailStatus ? emailStatus.configured : false;

  return (
    <AppShell activePage="comunicacao" title="Comunicacao">
      <div data-testid="communication-page">
        {showTemplateEditor && (
          <TemplateEditor template={editTemplate} onSave={handleSaveTemplate} onClose={function() { setShowTemplateEditor(false); setEditTemplate(null); }} loading={loading} />
        )}
        {showReminder && (
          <ReminderModal onSend={handleSendReminder} onClose={function() { setShowReminder(false); }} loading={loading} />
        )}

        {/* Toast */}
        {toast && (
          <div className={"fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all " + (toast.type === "success" ? "bg-emerald-500 text-white" : "bg-red-500 text-white")} data-testid="toast-message">
            {toast.msg}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <StatusBadge configured={configured} />
          <button onClick={function() { setShowReminder(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600"
            data-testid="trigger-reminders-button">
            <Bell className="w-4 h-4" /> Disparar Lembretes
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit" data-testid="comm-tabs">
          {[{k:"compose",l:"Novo E-mail",i:Send},{k:"templates",l:"Templates",i:FileText},{k:"history",l:"Historico",i:Mail}].map(function(t) {
            var Icon = t.i;
            return (
              <button key={t.k} onClick={function() { setTab(t.k); }}
                className={"flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors " + (tab === t.k ? "bg-white text-[#0A2F3D] shadow-sm" : "text-gray-500 hover:text-gray-700")}
                data-testid={"tab-" + t.k}>
                <Icon className="w-4 h-4" /> {t.l}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {tab === "compose" && (
          <ComposeEmail onSend={handleSendCampaign} onCancel={function() { setTab("history"); }} templates={templates} loading={loading} />
        )}

        {tab === "templates" && (
          <TemplatesTab templates={templates} onEdit={function(t) { setEditTemplate(t); setShowTemplateEditor(true); }} onDelete={handleDeleteTemplate} onNew={function() { setEditTemplate(null); setShowTemplateEditor(true); }} />
        )}

        {tab === "history" && (
          <HistoryTab campaigns={campaigns} onDelete={handleDeleteCampaign} />
        )}
      </div>
    </AppShell>
  );
}

function TemplatesTab({ templates, onEdit, onDelete, onNew }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{templates.length} template{templates.length !== 1 ? "s" : ""}</p>
        <button onClick={onNew} className="flex items-center gap-2 px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB]" data-testid="new-template-button">
          <Plus className="w-4 h-4" /> Novo Template
        </button>
      </div>
      {templates.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm text-center py-12">
          <FileText className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Nenhum template criado</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="templates-grid">
          {templates.map(function(t) {
            return (
              <div key={t.id} className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={"template-card-" + t.id}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-[#0A2F3D]">{t.title}</h4>
                    <p className="text-xs text-gray-400">{TYPE_LABELS[t.template_type] || t.template_type}</p>
                  </div>
                  <div className="flex gap-1">
                    {t.is_default && <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-[#3B82F6]/10 text-[#3B82F6]">Padrao</span>}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3 line-clamp-2">{t.subject}</p>
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button onClick={function() { onEdit(t); }} className="flex items-center gap-1 px-3 py-1.5 bg-[#3B82F6]/10 text-[#3B82F6] rounded-lg text-xs font-medium hover:bg-[#3B82F6]/20" data-testid={"template-edit-" + t.id}>
                    <Edit2 className="w-3 h-3" /> Editar
                  </button>
                  {!t.is_default && (
                    <button onClick={function() { onDelete(t.id); }} className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-500 rounded-lg text-xs font-medium hover:bg-red-100" data-testid={"template-delete-" + t.id}>
                      <Trash2 className="w-3 h-3" /> Excluir
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HistoryTab({ campaigns, onDelete }) {
  var STATUS_COLORS = {
    enviado: "bg-emerald-100 text-emerald-700", parcial: "bg-amber-100 text-amber-700",
    erro: "bg-red-100 text-red-700", enviando: "bg-blue-100 text-blue-700", rascunho: "bg-gray-100 text-gray-500",
  };
  var STATUS_LABELS = {
    enviado: "Enviado", parcial: "Parcial", erro: "Erro", enviando: "Enviando", rascunho: "Rascunho",
  };

  if (campaigns.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm text-center py-12">
        <Mail className="w-10 h-10 text-gray-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400">Nenhuma campanha enviada</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="campaigns-list">
      {campaigns.map(function(c) {
        var sCls = STATUS_COLORS[c.status] || "bg-gray-100 text-gray-500";
        var sLabel = STATUS_LABELS[c.status] || c.status;
        var dateStr = "";
        try { dateStr = new Date(c.sent_at || c.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch(e) {}
        return (
          <div key={c.id} className="bg-white rounded-xl shadow-sm p-5" data-testid={"campaign-card-" + c.id}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h4 className="font-semibold text-[#0A2F3D]">{c.subject}</h4>
                <p className="text-xs text-gray-400 mt-0.5">{dateStr} &bull; {GROUP_LABELS[c.recipient_group] || c.recipient_group} &bull; {c.sent_count} enviado{c.sent_count !== 1 ? "s" : ""}{c.error_count > 0 ? ", " + c.error_count + " erro" + (c.error_count !== 1 ? "s" : "") : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={"px-2 py-1 text-xs font-medium rounded-full " + sCls}>{sLabel}</span>
                <button onClick={function() { onDelete(c.id); }} className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={"campaign-delete-" + c.id}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
